<template>
  <div>
    <div class="flex items-center justify-between mb-4">
      <button @click="prevMonth" class="p-2 rounded-full bg-blue-50 hover:bg-blue-100">
        <span class="material-symbols-outlined">chevron_left</span>
      </button>
      <div class="font-semibold text-blue-900 text-base">
        {{ monthYearLabel }}
      </div>
      <button @click="nextMonth" class="p-2 rounded-full bg-blue-50 hover:bg-blue-100">
        <span class="material-symbols-outlined">chevron_right</span>
      </button>
    </div>
    <div class="overflow-x-auto">
      <div class="grid grid-cols-7 min-w-[700px]">
        <div
          v-for="(day, idx) in weekDayLabels"
          :key="idx"
          class="flex flex-col items-center border-b pb-2"
        >
          <div class="text-xs text-gray-500">{{ day }}</div>
        </div>
      </div>
      <div class="grid grid-cols-7 min-w-[700px]">
        <div
          v-for="(cell, idx) in calendarCells"
          :key="idx"
          class="min-h-[90px] border-r last:border-r-0 px-1 py-2"
        >
          <div v-if="cell">
            <div class="text-xs text-gray-700 mb-1 text-center" :class="isToday(cell.date) ? 'font-bold text-blue-700' : ''">
              {{ cell.day }}
            </div>
            <div v-if="testsByDay[cell.dateString] && testsByDay[cell.dateString].length > 0">
              <div
                v-for="test in testsByDay[cell.dateString]"
                :key="test.moduleCode + test.test + test.date"
                class="bg-blue-50 border-l-4 border-blue-700 rounded p-2 mb-2"
              >
                <div class="font-semibold text-blue-900 text-xs truncate">
                  {{ test.moduleCode }} - {{ test.test }}
                </div>
                <div class="text-xs text-gray-700">
                  {{ test.time }}<span v-if="test.venue">, {{ test.venue }}</span>
                </div>
                <div class="text-xs text-gray-500">
                  {{ test.campus }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { parseTestDate } from '@/composables/useTimetables';

export default {
  name: 'SemesterTestCalendar',
  props: {
    tests: {
      type: Array,
      default: () => []
    }
  },
  data() {
    const today = new Date();
    return {
      currentMonth: today.getMonth(),
      currentYear: today.getFullYear()
    };
  },
  computed: {
    weekDayLabels() {
      // Mon-Sun
      return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    },
    monthYearLabel() {
      const d = new Date(this.currentYear, this.currentMonth, 1);
      return d.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' });
    },
    calendarCells() {
      // Generate cells for the current month, aligned to week (Mon-Sun)
      const firstDay = new Date(this.currentYear, this.currentMonth, 1);
      const lastDay = new Date(this.currentYear, this.currentMonth + 1, 0);
      const firstWeekDay = (firstDay.getDay() + 6) % 7; // 0=Mon, 6=Sun
      const daysInMonth = lastDay.getDate();

      const cells = [];
      // Fill empty cells before the first day
      for (let i = 0; i < firstWeekDay; i++) {
        cells.push(null);
      }
      // Fill days of the month
      for (let d = 1; d <= daysInMonth; d++) {
        const date = new Date(this.currentYear, this.currentMonth, d);
        cells.push({
          day: d,
          date,
          dateString: date.toISOString().slice(0, 10)
        });
      }
      // Fill empty cells at the end to complete the last week
      while (cells.length % 7 !== 0) {
        cells.push(null);
      }
      return cells;
    },
    testsByDay() {
      // Group tests by date string (YYYY-MM-DD), only once per test
      const map = {};
      for (const test of this.tests) {
        const d = parseTestDate(test.date);
        const dateString = d.toISOString().slice(0, 10);
        if (!map[dateString]) map[dateString] = [];
        // Only push if not already present (avoid duplicates)
        if (!map[dateString].some(t => t.moduleCode === test.moduleCode && t.test === test.test && t.date === test.date)) {
          map[dateString].push(test);
        }
      }
      return map;
    }
  },
  methods: {
    prevMonth() {
      if (this.currentMonth === 0) {
        this.currentMonth = 11;
        this.currentYear--;
      } else {
        this.currentMonth--;
      }
    },
    nextMonth() {
      if (this.currentMonth === 11) {
        this.currentMonth = 0;
        this.currentYear++;
      } else {
        this.currentMonth++;
      }
    },
    isToday(date) {
      const today = new Date();
      return (
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()
      );
    }
  }
};
</script>

<style scoped>
.overflow-x-auto {
  -webkit-overflow-scrolling: touch;
}
</style>
